import { useCallback, useMemo, useRef } from "react";

type ItemRefsById = {
  [name: string | number]: HTMLElement;
};
type ItemRefsByIndex = {
  [index: number]: HTMLElement;
};

const defaultScrollBehavior: ScrollBehavior = "instant";
const defaultScrollPosition: ScrollLogicalPosition = "center";


const useScrollToManager = () => {
  const itemRefsById = useRef<ItemRefsById>({}); // Store refs for each item
  const itemRefsByIndex = useRef<ItemRefsByIndex>({}); // Store refs for each item by id

  const addScrollToItem = useCallback(
    (element: HTMLElement, id: string | number, index: number) => {
      itemRefsByIndex.current[index] = element;
      itemRefsById.current[id] = element;
    },
    [itemRefsByIndex, itemRefsById]
  );

  const scrollIntoView = useCallback(
    (
      element: HTMLElement,
      behavior: ScrollBehavior = "instant",
      block: ScrollLogicalPosition = "center"
    ) => {
      element &&
        element.scrollIntoView({
          behavior,
          block,
        });
    },
    []
  );

  const scrollToItemWithId = useCallback(
    (
      id: string | number,
      behavior: ScrollBehavior = defaultScrollBehavior,
      position: ScrollLogicalPosition = defaultScrollPosition
    ) => {
        scrollIntoView(itemRefsById.current[id], behavior, position);
    },
    [scrollIntoView]
  );

  const scrollToItemWithIndex = useCallback(
    (
        index: number,
      behavior: ScrollBehavior = defaultScrollBehavior,
      block: ScrollLogicalPosition = defaultScrollPosition
    ) => {
        scrollIntoView(itemRefsByIndex.current[index], behavior, block);
    },
    [scrollIntoView]
  );

  return useMemo(
    () => ({
      addScrollToItem,
      scrollToItemWithId,
      scrollToItemWithIndex,
    }),
    [addScrollToItem, scrollToItemWithId, scrollToItemWithIndex]
  );
};

export default useScrollToManager;
